import React, { useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PhoneInput from '../components/PhoneInput';
import { Eye, EyeOff, MessageSquare } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE_URL = `${BACKEND_URL}/api`;

const CustomerRegister = () => {
  const navigate = useNavigate();
  const { symbolicName } = useParams();
  
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    accepts_sms: false
  });
  const [loading, setLoading] = useState(false);
  const [calendarLoading, setCalendarLoading] = useState(true);
  const [calendarName, setCalendarName] = useState('');
  const [calendarId, setCalendarId] = useState('');  // Store calendar ID for scoped registration
  const [error, setError] = useState('');

  React.useEffect(() => {
    fetchCalendarInfo();
  }, [symbolicName]);

  const fetchCalendarInfo = async () => {
    try {
      setCalendarLoading(true);
      const response = await axios.get(`${API_BASE_URL}/public/calendars/${symbolicName}`);
      const calendar = response.data.data.calendar;
      setCalendarName(calendar.name);
      setCalendarId(calendar.id);  // Store the calendar ID
    } catch (err) {
      console.error('Error fetching calendar:', err);
      setCalendarName('this calendar');
    } finally {
      setCalendarLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handlePhoneChange = (value) => {
    setFormData({
      ...formData,
      phone: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      // CALENDAR-SCOPED REGISTRATION (RULE #3):
      // Customer registration includes calendar_id for scoped user creation
      // Same email can register at multiple different calendars
      const registerResponse = await axios.post(
        `${API_BASE_URL}/auth/register`,
        {
          full_name: formData.full_name,
          email: formData.email,
          phone: formData.phone,
          password: formData.password,
          role: 'customer',
          accepts_sms: formData.accepts_sms,
          calendar_id: calendarId  // Include calendar_id for scoped registration
        }
      );

      // Check if email verification is required
      if (registerResponse.data.requires_verification) {
        // Redirect to verification pending page with calendar info
        navigate('/verification-pending', { 
          state: { 
            email: registerResponse.data.email,
            calendarSymbolicName: symbolicName,
            isCustomer: true
          } 
        });
        return;
      }

      // Legacy flow - direct login (for admin-created users)
      const { access_token, user } = registerResponse.data;

      // Store token and user info
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(user));

      // Register customer to calendar (for legacy users without calendar_id set)
      try {
        await axios.post(
          `${API_BASE_URL}/calendar-registrations`,
          {
            customer_id: user.id,
            calendar_id: calendarId
          },
          { headers: { Authorization: `Bearer ${access_token}` } }
        );
      } catch (regError) {
        console.log('Calendar registration:', regError.response?.data?.detail || 'Registration error');
      }

      // Redirect to calendar view
      navigate(`/calendar/${symbolicName}`);
    } catch (err) {
      console.error('Registration error:', err);
      const errorDetail = err.response?.data?.detail;
      if (Array.isArray(errorDetail)) {
        const errorMessages = errorDetail.map(err => err.msg || 'Validation error').join(', ');
        setError(errorMessages || 'Please check your input and try again.');
      } else {
        setError(errorDetail || 'Failed to create account');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <section className="py-20 bg-gradient-to-br from-white to-amber-50">
        <div className="max-w-md mx-auto px-6">
          <div className="bg-white rounded-lg shadow-xl p-8">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-800 mb-2">Create Customer Account</h1>
              {calendarLoading ? (
                <p className="text-gray-600">Loading...</p>
              ) : (
                <p className="text-gray-600">Sign up for <span className="font-semibold text-yellow-600">{calendarName}</span></p>
              )}
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  placeholder="John Doe"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  placeholder="your.email@example.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <PhoneInput
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  className="w-full"
                  required
                  data-testid="customer-register-phone"
                />
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="accepts_sms"
                  name="accepts_sms"
                  checked={formData.accepts_sms}
                  onChange={(e) => setFormData({...formData, accepts_sms: e.target.checked})}
                  className="w-4 h-4 text-yellow-600 bg-gray-100 border-gray-300 rounded focus:ring-yellow-500 focus:ring-2"
                />
                <label htmlFor="accepts_sms" className="flex items-center text-sm text-gray-700">
                  <MessageSquare className="w-4 h-4 mr-2 text-gray-500" />
                  I agree to receive SMS notifications for appointments
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent pr-12"
                    placeholder="Create a password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent pr-12"
                    placeholder="Confirm your password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-white font-semibold py-3 px-4 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-gray-600">
                Already have an account?{' '}
                <Link
                  to={`/customer/login/${symbolicName}`}
                  className="text-yellow-600 hover:text-yellow-700 font-semibold"
                >
                  Sign In
                </Link>
              </p>
            </div>

            <div className="mt-4 text-center">
              <Link
                to="/find-calendar"
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                ← Back to Find Calendar
              </Link>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default CustomerRegister;
